import { Box, Card, Typography, IconButton, useTheme } from '@mui/material';
import { InfoOutlined, CloseOutlined } from '@mui/icons-material';
import { useState, useMemo } from 'react';
import DynamicMuiIcon from '@/components/common/dynamic-mui-icon';
import { isValidIconName, type IconName } from '@/components/common/dynamic-mui-icon.constants';
import EventForm from '@/components/create-wizard/tab-row-item/row-forms/create-event.form';
import CreateOrganizationModal from '@/components/organizations/user-organizations/create-organization-modal';
import { useTranslations } from '@/i18n/use-translations';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useFeatureAccess } from '@/hooks/use-feature-access';

interface CreateOverlayTile {
  id: string;
  title: string;
  description: string;
  detailedInfo: string;
  additionalInfo?: string;
  icon: IconName;
  color: string;
}

const useCreateOverlayTiles = (): CreateOverlayTile[] => {
  const t = useTranslations();
  const { hasAccess: hasCreateOrganizationPermission } = useFeatureAccess('create-organization');

  return useMemo(
    () => [
      {
        id: 'event',
        title: t('createOverlay.event.title'),
        description: t('createOverlay.event.description'),
        detailedInfo: t('createOverlay.event.detailedInfo'),
        additionalInfo: t('createOverlay.event.additionalInfo'),
        icon: 'FestivalOutlined',
        color: '#1976d2',
      },
      ...(hasCreateOrganizationPermission
        ? [
            {
              id: 'organization',
              title: t('createOverlay.organization.title'),
              description: t('createOverlay.organization.description'),
              detailedInfo: t('createOverlay.organization.detailedInfo'),
              additionalInfo: t('createOverlay.organization.additionalInfo'),
              icon: 'Groups2Outlined' as const,
              color: '#5c6bc0',
            },
          ]
        : []),
      {
        id: 'content',
        title: t('createOverlay.content.title'),
        description: t('createOverlay.content.description'),
        detailedInfo: t('createOverlay.content.detailedInfo'),
        additionalInfo: t('createOverlay.content.additionalInfo'),
        icon: 'VideoSettingsOutlined',
        color: '#7b1fa2',
      },
      {
        id: 'location',
        title: t('createOverlay.location.title'),
        description: t('createOverlay.location.description'),
        detailedInfo: t('createOverlay.location.detailedInfo'),
        additionalInfo: t('createOverlay.location.additionalInfo'),
        icon: 'BadgeOutlined',
        color: '#388e3c',
      },
      {
        id: 'service',
        title: t('createOverlay.service.title'),
        description: t('createOverlay.service.description'),
        detailedInfo: t('createOverlay.service.detailedInfo'),
        additionalInfo: t('createOverlay.service.additionalInfo'),
        icon: 'SupportAgentOutlined',
        color: '#f57c00',
      },
      {
        id: 'contribution',
        title: t('createOverlay.contribution.title'),
        description: t('createOverlay.contribution.description'),
        detailedInfo: t('createOverlay.contribution.detailedInfo'),
        additionalInfo: t('createOverlay.contribution.additionalInfo'),
        icon: 'CoPresentOutlined',
        color: '#0097a7',
      },
    ],
    [t, hasCreateOrganizationPermission],
  );
};

const CreateOverlayTileCard = ({ tile }: { tile: CreateOverlayTile }) => {
  const theme = useTheme();
  const isDesktop = useBreakpointUp('md');
  const [isFlipped, setIsFlipped] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isOrganizationModalOpen, setIsOrganizationModalOpen] = useState(false);

  // Helper function to convert hex color to rgba with opacity
  const getTileBackgroundColor = (hexColor: string, isDark: boolean) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const opacity = isDark ? 0.12 : 0.08;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const getTileBackgroundColorFlipped = (hexColor: string, isDark: boolean) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const opacity = isDark ? 0.16 : 0.12;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFlipped(!isFlipped);
  };

  const handleTileClick = (e: React.MouseEvent) => {
    // Don't open modal if clicking the info icon
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('[role="button"]')) {
      return;
    }
    if (tile.id === 'event') {
      e.preventDefault();
      e.stopPropagation();
      setIsEventModalOpen(true);
    }
    if (tile.id === 'organization') {
      e.preventDefault();
      e.stopPropagation();
      setIsOrganizationModalOpen(true);
    }
  };

  const handleEventModalClose = () => {
    setIsEventModalOpen(false);
  };

  const handleEventModalSuccess = () => {
    setIsEventModalOpen(false);
  };

  const handleOrganizationModalClose = () => {
    setIsOrganizationModalOpen(false);
  };

  const isClickableTile = tile.id === 'event' || tile.id === 'organization';

  return (
    <Box
      sx={{
        flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(25% - 12px)' },
        minWidth: { xs: 'calc(100% - 8px)', sm: '200px', md: '300px' },
        maxWidth: { xs: '100%', sm: '400px', md: '500px' },
        perspective: '1000px',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: { xs: '180px', sm: '200px', md: '220px' },
          transformStyle: 'preserve-3d',
          transition: 'transform 0.6s',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
        onMouseEnter={() => {
          if (isDesktop && !isClickableTile) {
            setIsFlipped(true);
          }
        }}
        onMouseLeave={() => {
          if (isDesktop && !isClickableTile) {
            setIsFlipped(false);
          }
        }}
      >
        {/* Front Side */}
        <Card
          onClick={handleTileClick}
          sx={{
            'width': '100%',
            'minHeight': { xs: '180px', sm: '200px', md: '220px' },
            'textAlign': 'left',
            'cursor': isClickableTile ? 'pointer' : 'default',
            'border': 1,
            'borderColor': 'divider',
            'borderRadius': 2,
            'backgroundColor': getTileBackgroundColor(tile.color, theme.palette.mode === 'dark'),
            'boxShadow': 1,
            'transition': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            'display': 'flex',
            'flexDirection': 'column',
            'p': 3,
            'position': 'relative',
            'overflow': 'hidden',
            'backfaceVisibility': 'hidden',
            'WebkitBackfaceVisibility': 'hidden',
            'pointerEvents': 'auto',
            '&:hover': {
              boxShadow: 4,
              transform: isDesktop ? 'none' : 'translateY(-4px)',
              borderColor: tile.color,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: tile.color,
              transform: 'scaleX(0)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            },
            '&:hover::before': {
              transform: 'scaleX(1)',
            },
          }}
          variant="outlined"
        >
          <IconButton
            onClick={handleInfoClick}
            sx={{
              'position': 'absolute',
              'top': 8,
              'right': 8,
              'color': 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
                color: tile.color,
              },
            }}
            size="small"
          >
            <InfoOutlined fontSize="small" />
          </IconButton>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: tile.additionalInfo && tile.additionalInfo.trim() ? 1.5 : 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                height: 64,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: 1,
                flexShrink: 0,
              }}
            >
              <DynamicMuiIcon
                iconName={isValidIconName(tile.icon) ? tile.icon : 'HelpOutline'}
                sx={{ color: tile.color, fontSize: 32 }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1,
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                }}
              >
                {tile.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.5,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                }}
              >
                {tile.description}
              </Typography>
            </Box>
          </Box>
          {tile.additionalInfo && tile.additionalInfo.trim() && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.5,
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                fontStyle: 'italic',
                whiteSpace: 'pre-line',
                width: '100%',
              }}
            >
              {tile.additionalInfo}
            </Typography>
          )}
        </Card>

        {/* Back Side */}
        <Card
          onClick={handleTileClick}
          sx={{
            width: '100%',
            minHeight: { xs: '180px', sm: '200px', md: '220px' },
            textAlign: 'left',
            cursor: tile.id === 'event' ? 'pointer' : 'default',
            border: 1,
            borderColor: tile.color,
            borderRadius: 2,
            backgroundColor: getTileBackgroundColorFlipped(tile.color, theme.palette.mode === 'dark'),
            boxShadow: 4,
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            overflow: 'auto',
            pointerEvents: 'auto',
          }}
          variant="outlined"
        >
          <IconButton
            onClick={handleInfoClick}
            sx={{
              'position': 'absolute',
              'top': 8,
              'right': 8,
              'color': 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
                color: tile.color,
              },
            }}
            size="small"
          >
            <CloseOutlined fontSize="small" />
          </IconButton>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1.5,
                fontSize: { xs: '1rem', sm: '1.125rem' },
              }}
            >
              {tile.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.6,
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              }}
            >
              {tile.detailedInfo}
            </Typography>
          </Box>
        </Card>
      </Box>
      {tile.id === 'event' && isEventModalOpen && (
        <EventForm onClose={handleEventModalClose} onSuccess={handleEventModalSuccess} />
      )}
      {tile.id === 'organization' && (
        <CreateOrganizationModal
          open={isOrganizationModalOpen}
          onClose={handleOrganizationModalClose}
          onSuccess={handleOrganizationModalClose}
        />
      )}
    </Box>
  );
};

export default function CreateOverlay() {
  const createOverlayTiles = useCreateOverlayTiles();

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        width: '100%',
        pt: { xs: 2, md: 2 },
      }}
    >
      {createOverlayTiles.map((tile) => (
        <CreateOverlayTileCard key={tile.id} tile={tile} />
      ))}
    </Box>
  );
}
